<template>
  <v-dialog v-model="dialog" max-width="900px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
    <v-card class="d-flex flex-column" style="max-height: 90vh;">
      <v-card-title class="d-flex justify-space-between align-center py-3">
        <span class="text-h6 font-weight-bold">Importar Nota Fiscal (Modelo 55)</span>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-0 flex-grow-1 overflow-y-auto">
        <!-- EMPTY STATE: UPLOAD ZONE -->
        <v-container v-if="!xmlParsed" class="fill-height d-flex flex-column justify-center align-center" style="min-height: 400px;">
          <v-sheet
            color="grey lighten-4"
            class="d-flex flex-column align-center justify-center pa-10 rounded-lg dashed-border cursor-pointer transition-swing"
            width="100%"
            max-width="600"
            elevation="0"
            @click="$refs.fileInput.$refs.input.click()"
            v-ripple
          >
            <v-icon size="64" color="primary" class="mb-4">mdi-cloud-upload-outline</v-icon>
            <h3 class="text-h6 font-weight-medium text-center mb-2">
              Importar XML da Nota Fiscal
            </h3>
            <p class="text-body-2 grey--text text--darken-1 text-center mb-6">
              Clique para selecionar ou arraste o arquivo XML para esta área
            </p>
            
            <v-btn color="primary" outlined class="text-capitalize px-6">
              Selecionar Arquivo
            </v-btn>

            <!-- Hidden File Input -->
            <v-file-input
              ref="fileInput"
              class="d-none"
              accept=".xml"
              v-model="xmlFile"
              @change="handleXmlUpload"
            ></v-file-input>
          </v-sheet>
        </v-container>

        <!-- LOADED STATE: SUMMARY & TABS -->
        <div v-else class="d-flex flex-column h-100">
          <!-- Summary Card -->
          <div class="pa-4 pb-0">
             <v-card outlined class="pa-4 mb-4 rounded-lg bg-light-blue">
              <div class="d-flex flex-wrap align-center justify-space-between">
                <div>
                   <div class="text-overline grey--text text--darken-1 mb-0">Fornecedor</div>
                   <div class="text-subtitle-1 font-weight-bold text-truncate" style="max-width: 300px;">
                      {{ invoice.emitente.nome }}
                   </div>
                   <div class="caption grey--text">{{ invoice.emitente.cnpj }}</div>
                </div>
                 <div class="text-right mx-4">
                   <div class="text-overline grey--text text--darken-1 mb-0">Nota Fiscal</div>
                   <div class="text-h6 primary--text">Nº {{ invoice.numero }}</div>
                   <div class="caption grey--text">Série {{ invoice.serie }}</div>
                </div>
                <div class="text-right">
                   <div class="text-overline grey--text text--darken-1 mb-0">Valor Total</div>
                   <div class="text-h5 success--text font-weight-bold">{{ invoice.valorTotal }}</div>
                   <div class="caption grey--text">{{ invoice.dataEmissao }}</div>
                </div>
              </div>
            </v-card>
          </div>

          <!-- Tabs -->
          <v-tabs v-model="activeTab" grow background-color="transparent" color="primary">
            <v-tab>
              <v-icon left small>mdi-package-variant-closed</v-icon>
              Itens ({{ invoice.produtos.length }})
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-file-document-outline</v-icon>
              Detalhes da Nota
            </v-tab>
          </v-tabs>
          
          <v-divider></v-divider>

          <v-tabs-items v-model="activeTab" class="flex-grow-1">
             <!-- TAB 1: ITENS -->
             <v-tab-item>
               <v-container fluid class="pa-0">
                  <v-data-table
                    :headers="productHeaders"
                    :items="invoice.produtos"
                    dense
                    disable-sort
                    hide-default-footer
                    disable-pagination
                    class="elevation-0"
                    mobile-breakpoint="0"
                  >
                    <template v-slot:item.exists="{ item }">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                            class="mr-2"
                            :color="item.exists ? 'success' : 'grey lighten-1'"
                          >
                            {{ item.exists ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}
                          </v-icon>
                        </template>
                         <span>{{ item.exists ? "Produto já cadastrado" : "Produto não encontrado" }}</span>
                      </v-tooltip>
                    </template>

                    <template v-slot:item.lotes="{ item }">
                      <v-menu v-if="item.lotes && item.lotes.length > 0" offset-y open-on-hover>
                        <template v-slot:activator="{ on, attrs }">
                          <v-chip
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="primary"
                            outlined
                          >
                            <v-icon left x-small>mdi-package-variant</v-icon>
                            {{ item.lotes.length }}
                          </v-chip>
                        </template>
                        <v-card class="pa-3" max-width="350">
                          <div class="text-subtitle-2 font-weight-bold mb-2">
                            <v-icon small class="mr-1">mdi-package-variant</v-icon>
                            Lotes ({{ item.lotes.length }})
                          </div>
                          <v-divider class="mb-2"></v-divider>
                          <div v-for="(lote, idx) in item.lotes" :key="idx" class="mb-2">
                            <div class="d-flex justify-space-between">
                              <span class="font-weight-medium">{{ lote.nLote || 'S/N' }}</span>
                              <span class="primary--text">{{ lote.qLote }} un</span>
                            </div>
                            <div class="caption grey--text">
                              <span v-if="lote.dFab">Fab: {{ formatDateBr(lote.dFab) }}</span>
                              <span v-if="lote.dVal" class="ml-2">Val: {{ formatDateBr(lote.dVal) }}</span>
                            </div>
                          </div>
                        </v-card>
                      </v-menu>
                      <span v-else class="grey--text">-</span>
                    </template>
                  </v-data-table>
               </v-container>
             </v-tab-item>

             <!-- TAB 2: DETALHES -->
             <v-tab-item>
               <v-container fluid class="pa-6">
                 <v-row>
                   <v-col cols="12">
                     <div class="text-overline mb-2">Chave de Acesso</div>
                     <v-sheetOutlined class="pa-3 rounded grey lighten-4 text-break font-monospace">
                       {{ invoice.chave }}
                     </v-sheetOutlined>
                   </v-col>
                   
                   <v-col cols="12" sm="6">
                     <v-list-item two-line class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Código de Autorização</v-list-item-subtitle>
                          <v-list-item-title>{{ invoice.codAutorizacao }}</v-list-item-title>
                        </v-list-item-content>
                     </v-list-item>
                   </v-col>
                   
                    <v-col cols="12" sm="6">
                     <v-list-item two-line class="px-0">
                        <v-list-item-content>
                          <v-list-item-subtitle>Endereço do Fornecedor</v-list-item-subtitle>
                          <v-list-item-title class="text-wrap">
                            {{ formatAddress(invoice.emitente.endereco) }}
                          </v-list-item-title>
                        </v-list-item-content>
                     </v-list-item>
                   </v-col>

                   <v-col cols="12">
                      <div class="text-overline mb-2">Informações Complementares</div>
                      <p class="body-2 grey--text text--darken-2">
                        {{ invoice.infComplementar || 'Nenhuma informação complementar.' }}
                      </p>
                   </v-col>
                 </v-row>
               </v-container>
             </v-tab-item>
          </v-tabs-items>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4 grey lighten-5">
        <v-btn text color="error" @click="resetForm" v-if="xmlParsed">
          <v-icon left>mdi-trash-can-outline</v-icon>
          Limpar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text class="text-capitalize" @click="closeDialog">Cancelar</v-btn>
        <v-btn
          color="success"
          elevation="0"
          :disabled="!xmlParsed"
          class="text-capitalize px-6"
          @click="confirmImport"
        >
          Confirmar Importação
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "NfeEntryModal",
  data() {
    return {
      dialog: false,
      xmlFile: null,
      xmlParsed: false,
      activeTab: 0,
      invoice: {
        chave: "",
        numero: "",
        serie: "",
        codAutorizacao: "",
        dataEmissao: "",
        valorTotal: "",
        infComplementar: "",
        emitente: {
          cnpj: "",
          nome: "",
          endereco: {},
        },
        produtos: [],
      },
      productHeaders: [
        { text: "", value: "exists", width: "40px", sortable: false },
        { text: "Cód.", value: "cProd" },
        { text: "Nome do Produto", value: "nome", width: "25%" },
        { text: "NCM", value: "ncm" },
        { text: "CFOP", value: "cfop" },
        { text: "UN", value: "unidadeComercial" },
        { text: "Qtd.", value: "quantidade", align: 'end' },
        { text: "Vlr. Unit.", value: "valorUnitario", align: 'end' },
        { text: "Lotes", value: "lotes", align: 'center', width: "100px" },
      ],
    };
  },
  methods: {
    openDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      setTimeout(() => {
        this.resetForm();
      }, 300);
    },
    resetForm() {
      this.xmlFile = null;
      this.xmlParsed = false;
      this.activeTab = 0;
      this.invoice = {
        chave: "",
        numero: "",
        serie: "",
        codAutorizacao: "",
        dataEmissao: "",
        valorTotal: "",
        infComplementar: "",
        emitente: {
          cnpj: "",
          nome: "",
          endereco: {},
        },
        produtos: [],
      };
    },
    async handleXmlUpload(file) {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        await this.parseXmlData(text);
      };
      reader.readAsText(file);
    },
    formatAddress(addr) {
      if (!addr || !addr.rua) return '-';
      return `${addr.rua}, ${addr.numero} - ${addr.bairro}, ${addr.cidade} - ${addr.estado}, ${addr.cep}`;
    },
    async parseXmlData(xmlContent) {
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, "application/xml");

        const infNFe = xmlDoc.getElementsByTagName("infNFe")[0];
        const protNFe = xmlDoc.getElementsByTagName("infProt")[0];
        
        if (!infNFe) throw new Error("XML inválido: tag infNFe não encontrada.");

        this.invoice = {
          chave: infNFe.getAttribute("Id")?.replace('NFe', '') || '',
          numero: infNFe.getElementsByTagName("nNF")[0]?.textContent,
          serie: infNFe.getElementsByTagName("serie")[0]?.textContent,
          codAutorizacao: protNFe?.getElementsByTagName("nProt")[0]?.textContent || '',
          dataEmissao: this.formatDate(infNFe.getElementsByTagName("dhEmi")[0]?.textContent),
          valorTotal: this.moneyBr(
            xmlDoc.getElementsByTagName("vNF")[0]?.textContent
          ),
          infComplementar: xmlDoc.getElementsByTagName("infCpl")[0]?.textContent || "",
          emitente: {
            cnpj: infNFe.getElementsByTagName("CNPJ")[0]?.textContent,
            nome: infNFe.getElementsByTagName("xNome")[0]?.textContent,
            endereco:{
              rua: infNFe.getElementsByTagName("xLgr")[0]?.textContent,
              numero: infNFe.getElementsByTagName("nro")[0]?.textContent,
              bairro: infNFe.getElementsByTagName("xBairro")[0]?.textContent,
              cidade: infNFe.getElementsByTagName("xMun")[0]?.textContent,
              estado: infNFe.getElementsByTagName("UF")[0]?.textContent,
              cep: infNFe.getElementsByTagName("CEP")[0]?.textContent,
            },
          },
          produtos: await this.parseProducts(xmlDoc),
        };

        this.xmlParsed = true;
      } catch (error) {
        console.error(error);
        alert("Erro ao ler o arquivo XML. Verifique se é uma NFe válida.");
        this.resetForm();
      }
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },
    formatDateBr(dateString) {
      if (!dateString) return '';
      // Formato esperado: YYYY-MM-DD
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dateString;
    },
    async parseProducts(xmlDoc) {
      const detTags = xmlDoc.getElementsByTagName("det");
      const produtos = [];

      for (let i = 0; i < detTags.length; i++) {
        const prod = detTags[i].getElementsByTagName("prod")[0];
        const imposto = detTags[i].getElementsByTagName("imposto")[0];

        // Extrair ICMS CST/CSOSN dinamicamente
        const icmsData = this.extractIcmsData(imposto);
        const pisData = this.extractPisData(imposto);
        const cofinsData = this.extractCofinsData(imposto);

        const ean = prod.getElementsByTagName("cEAN")[0]?.textContent || null;
        const supplierCode = prod.getElementsByTagName("cProd")[0]?.textContent || null;

        const exists = await this.checkProductExists(ean, supplierCode);

        // Extrair dados de rastreabilidade/lote (<rastro>)
        const lotes = this.extractBatchData(prod);

        produtos.push({
          cProd: prod.getElementsByTagName("cProd")[0]?.textContent,
          nome: prod.getElementsByTagName("xProd")[0]?.textContent,
          valorUnitario: this.moneyBr(
            prod.getElementsByTagName("vUnCom")[0]?.textContent
          ),
          quantidade: parseFloat(
            prod.getElementsByTagName("qCom")[0]?.textContent
          ),
          ncm: prod.getElementsByTagName("NCM")[0]?.textContent,
          cest: prod.getElementsByTagName("CEST")[0]?.textContent || "",
          gtin: prod.getElementsByTagName("cEAN")[0]?.textContent || "SEM GTIN",
          cfop: prod.getElementsByTagName("CFOP")[0]?.textContent,
          unidadeComercial: prod.getElementsByTagName("uCom")[0]?.textContent,
          origem: icmsData.origem,
          icmsCst: icmsData.cst,
          csosn: icmsData.csosn,
          icmsAliquota: icmsData.aliquota,
          pisCst: pisData.cst,
          pisAliquota: pisData.aliquota,
          cofinsCst: cofinsData.cst,
          cofinsAliquota: cofinsData.aliquota,
          lotes: lotes,
          exists,
        });
      }

      return produtos;
    },

    // Extrai CST/CSOSN e aliquota do ICMS de qualquer variação de tag
    extractIcmsData(impostoElement) {
      if (!impostoElement) return { cst: "", csosn: "", aliquota: "0", origem: "0" };

      const icmsElement = impostoElement.getElementsByTagName("ICMS")[0];
      if (!icmsElement) return { cst: "", csosn: "", aliquota: "0", origem: "0" };

      // Tags do Simples Nacional (CSOSN)
      const csosnTags = ['ICMSSN101', 'ICMSSN102', 'ICMSSN201', 'ICMSSN202', 'ICMSSN500', 'ICMSSN900'];
      for (const tag of csosnTags) {
        const elem = icmsElement.getElementsByTagName(tag)[0];
        if (elem) {
          return {
            cst: "",
            csosn: elem.getElementsByTagName("CSOSN")[0]?.textContent || "",
            aliquota: elem.getElementsByTagName("pICMS")[0]?.textContent || "0",
            origem: elem.getElementsByTagName("orig")[0]?.textContent || "0"
          };
        }
      }

      // Tags do Regime Normal (CST)
      const cstTags = ['ICMS00', 'ICMS10', 'ICMS20', 'ICMS30', 'ICMS40', 'ICMS41', 'ICMS50', 'ICMS51', 'ICMS60', 'ICMS70', 'ICMS90'];
      for (const tag of cstTags) {
        const elem = icmsElement.getElementsByTagName(tag)[0];
        if (elem) {
          return {
            cst: elem.getElementsByTagName("CST")[0]?.textContent || "",
            csosn: "",
            aliquota: elem.getElementsByTagName("pICMS")[0]?.textContent || "0",
            origem: elem.getElementsByTagName("orig")[0]?.textContent || "0"
          };
        }
      }

      return { cst: "", csosn: "", aliquota: "0", origem: "0" };
    },

    extractPisData(impostoElement) {
      if (!impostoElement) return { cst: "", aliquota: "0" };

      const pisElement = impostoElement.getElementsByTagName("PIS")[0];
      if (!pisElement) return { cst: "", aliquota: "0" };

      const pisTags = ['PISAliq', 'PISQtde', 'PISNT', 'PISOutr'];
      for (const tag of pisTags) {
        const elem = pisElement.getElementsByTagName(tag)[0];
        if (elem) {
          return {
            cst: elem.getElementsByTagName("CST")[0]?.textContent || "",
            aliquota: elem.getElementsByTagName("pPIS")[0]?.textContent || "0"
          };
        }
      }

      return { cst: "", aliquota: "0" };
    },

    extractCofinsData(impostoElement) {
      if (!impostoElement) return { cst: "", aliquota: "0" };

      const cofinsElement = impostoElement.getElementsByTagName("COFINS")[0];
      if (!cofinsElement) return { cst: "", aliquota: "0" };

      const cofinsTags = ['COFINSAliq', 'COFINSQtde', 'COFINSNT', 'COFINSOutr'];
      for (const tag of cofinsTags) {
        const elem = cofinsElement.getElementsByTagName(tag)[0];
        if (elem) {
          return {
            cst: elem.getElementsByTagName("CST")[0]?.textContent || "",
            aliquota: elem.getElementsByTagName("pCOFINS")[0]?.textContent || "0"
          };
        }
      }

      return { cst: "", aliquota: "0" };
    },

    // Extrai dados de rastreabilidade/lote (<rastro>) do produto
    extractBatchData(prodElement) {
      if (!prodElement) return [];

      const rastroElements = prodElement.getElementsByTagName("rastro");
      const lotes = [];

      for (let i = 0; i < rastroElements.length; i++) {
        const rastro = rastroElements[i];
        lotes.push({
          nLote: rastro.getElementsByTagName("nLote")[0]?.textContent || null,
          qLote: parseFloat(rastro.getElementsByTagName("qLote")[0]?.textContent || 0),
          dFab: rastro.getElementsByTagName("dFab")[0]?.textContent || null,
          dVal: rastro.getElementsByTagName("dVal")[0]?.textContent || null,
        });
      }

      return lotes;
    },

    moneyBr(value) {
      if (!value) {
        var value = 0;
      }

      var parsedValue = parseFloat(value);
      var isNegative = parsedValue < 0;
      var value = Math.abs(parsedValue).toFixed(2).split(".");

      value[0] = value[0].split(/(?=(?:...)*$)/).join(".");

      return "R$ " + (isNegative ? "-" : "") + value.join(",");
    },

    async confirmImport() {
      if (!this.xmlParsed) {
        alert("Nenhum XML foi carregado.");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("invoice", JSON.stringify(this.invoice));
        formData.append("xmlFile", this.xmlFile);

        const response = await this.$http.$post("/invoice/import", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$emit('xmlImported', response.data);
        this.closeDialog();

        // Verifica se houve warnings (itens não importados)
        if (response.warnings && response.warnings.items_skipped?.length > 0) {
          const skippedCount = response.warnings.items_skipped.length;
          this.$toast.warning(`Nota importada com ${skippedCount} item(s) não processado(s).`);
        } else {
          this.$toast.success("Nota fiscal importada com sucesso!");
        }

        // Mostra resumo se disponível
        if (response.import_summary) {
          console.log('Import Summary:', response.import_summary);
        }

      } catch (error) {
        console.error("Erro ao importar a nota:", error);

        // Extrai mensagem de erro detalhada da resposta
        const errorMessage = error.response?.data?.message || "Erro ao importar nota fiscal.";
        const errorCode = error.response?.data?.error_code || "UNKNOWN";

        // Mensagens específicas por código de erro
        const errorMessages = {
          'DUPLICATE_INVOICE': 'Esta nota fiscal já foi importada anteriormente.',
          'MISSING_FIELD': errorMessage,
          'COMPANY_NOT_FOUND': 'Empresa não configurada. Configure sua empresa antes de importar.',
          'INVALID_CNPJ': 'CNPJ do fornecedor inválido ou ausente.',
        };

        this.$toast.error(errorMessages[errorCode] || errorMessage);
      }
    },

    async checkProductExists(ean, supplierCode) {
      try {
        const response = await this.$http.$get("/products/check", {
          params: {
            ean: ean,
            supplier_code: supplierCode
          }
        });
        return response.exists;
      } catch (error) {
        // Fallback para busca local se API falhar
        if (this.$store && this.$store.getters["product/products"]) {
           const products = this.$store.getters["product/products"];
           return products.some((p) => p.sku === supplierCode || p.bar_code === ean);
        }
        return false;
      }
    },
  },
};
</script>

<style scoped>
.dashed-border {
  border: 2px dashed #e0e0e0;
}
.dashed-border:hover {
  border-color: #1976d2;
  background-color: #f5f9ff !important;
}
.cursor-pointer {
  cursor: pointer;
}
.bg-light-blue {
    background-color: #f0f7ff !important;
    border-color: #bbdefb !important;
}
</style>
